import { useState } from "react";
import { ChevronRight, RefreshCw, Link as LinkIcon, Image as ImageIcon, Compass, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "../utils/constants";
import FormShell from "../components/FormShell";
import QuestionFlow from "../components/QuestionFlow";
import { applySourceAnswers } from "../utils/sourceAnswers";
import "./InputPage.css";

const InputPage = ({ onNext, onBack, userInput, setUserInput, startOver, onSkipToMenu,
  selectedCities = [], setSelectedCities = () => {}, navigateTo = () => {} }) => {
  // a page or screenshot they brought: what we read, and the one question that decides what to take (core/source_questions)
  const [sourceAsk, setSourceAsk] = useState(null);
  const cityNames = (selectedCities || []).map(c => c?.name).filter(Boolean);
  const [inputMode, setInputMode] = useState(null);
  const [textInput, setTextInput] = useState(userInput.text || "");
  const [urlInput, setUrlInput] = useState(userInput.url || "");
  const [isUploading, setIsUploading] = useState(false);
  const [isReadingLink, setIsReadingLink] = useState(false);
  const [sourceError, setSourceError] = useState("");

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setSourceError("");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("cities", cityNames.join(","));
    try {
      const res = await axios.post(`${API}/extract/image`, formData);
      if ((res.data.questions || []).length) {
        setSourceAsk({ source: "image", reading: res.data.reading, questions: res.data.questions, file: file.name });
      } else {
        setUserInput(u => ({ ...u, imagePlaces: res.data.places, imageFile: file.name, imageAsked: false, imageMusts: [] }));
      }
      setInputMode(null);
    } catch (e) {
      // the server says why (not an image, too large, could not be read) — never a silent "0 found"
      setSourceError(e.response?.data?.detail || "We couldn't read that screenshot — try again, or type the places.");
    } finally { setIsUploading(false); }
  };

  // A link is read when they continue, the way a screenshot is read when they upload it: its places join
  // the wishlist (2026-10-07: the link was sent along and nobody read it).
  const saveAndContinue = async () => {
    const link = urlInput.trim();
    let linkPlaces = link ? (userInput.linkPlaces || []) : [];
    if (link && link !== userInput.linkRead) {
      setIsReadingLink(true);
      setSourceError("");
      try {
        const res = await axios.post(`${API}/extract/url`, { url: link, cities: cityNames });
        linkPlaces = res.data.places || [];
        if ((res.data.questions || []).length) {
          setIsReadingLink(false);
          setSourceAsk({ source: "link", reading: res.data.reading, questions: res.data.questions, link });
          return;
        }
      } catch (e) {
        setIsReadingLink(false);
        setSourceError(e.response?.data?.detail || "We couldn't read that link — check it, or paste the places as text.");
        return;
      }
      setIsReadingLink(false);
    }
    setUserInput(u => ({ ...u, text: textInput, url: link, linkPlaces, linkMusts: link ? (u.linkMusts || []) : [], linkRead: link }));
    onNext();
  };

  // their answer to the page's question: ticked places are theirs, the rest are ideas; a stay is the base
  const finishSource = (answers) => {
    const ask = sourceAsk;
    setSourceAsk(null);
    const got = applySourceAnswers(ask.reading, answers);
    const named = list => (ask.reading?.places || []).filter(p => list.includes(p.name));
    const text = got.note ? [textInput, got.note].filter(Boolean).join("\n") : textInput;
    if (got.note) setTextInput(text);
    if (got.base) {
      setSelectedCities((selectedCities || []).map(c => (c?.name === got.base.city ? { ...c, base: got.base.name } : c)));
    }
    if (ask.source === "image") {
      setUserInput(u => ({ ...u, text, imageFile: ask.file, imageAsked: true, imageMusts: got.musts, imagePlaces: named(got.ideas) }));
    } else {
      setUserInput(u => ({ ...u, text, url: ask.link, linkPlaces: named(got.ideas), linkMusts: got.musts, linkRead: ask.link, linkAbout: ask.reading?.about || "" }));
    }
    if (got.addCity) { navigateTo("cities"); return; }
    if (ask.source === "link") onNext();
  };

  const handleUnfold = () => {
    setUserInput(u => ({ ...u, unfoldMode: true, text: "", url: "", linkPlaces: [], linkMusts: [], linkRead: "" }));
    onNext();
  };

  const skipInput = () => {
    onSkipToMenu();
  };

  if (sourceAsk) return <QuestionFlow questions={sourceAsk.questions} onDone={finishSource} />;

  return (
    <FormShell
      step={7}
      photo="/photos/onboard-input"
      photoPos="47% 100%"
      eyebrow="WHAT'S ON YOUR WISHLIST?"
      bigWord="WISHLIST"
      title="What's on your wishlist?"
      subtitle="What would make this trip feel like yours?"
      onBack={() => { setUserInput(u => ({ ...u, text: textInput, url: urlInput })); onBack(); }}
      onStartOver={startOver}
      showContinue={false}
    >
      {/* Main text input */}
      <div className="wishlist-field">
        <label htmlFor="wishlist-text" className="wishlist-label">Your wishlist</label>
        <textarea id="wishlist-text" value={textInput} onChange={(e) => setTextInput(e.target.value)}
          placeholder="Type here..."
          aria-describedby="wishlist-hint"
          className="wishlist-textarea"
          data-testid="wishlist-text-input" />
        <p id="wishlist-hint" className="wishlist-hint">Places you love, things to skip, your kind of day. A few words are enough.</p>
      </div>

      {/* Upload options */}
      <div className="flex gap-3">
        <label className="flex-1 cursor-pointer">
          <div className="p-4 transition-all flex items-center gap-3 hover:shadow-sm"
            style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4 }}>
            <ImageIcon className="w-5 h-5" style={{ color: "var(--zen-accent)" }} />
            <div>
              <p className="font-medium text-sm" style={{ color: "var(--zen-text)" }}>{userInput.imageFile || "Upload screenshot"}</p>
              <p className="text-xs" style={{ color: "var(--zen-sub)" }}>Saved ideas or notes</p>
            </div>
            {isUploading && <RefreshCw className="w-4 h-4 animate-spin ml-auto" style={{ color: "var(--zen-accent)" }} />}
            {userInput.imagePlaces?.length > 0 && <span className="ml-auto text-xs" style={{ color: "var(--zen-accent)" }}>{userInput.imagePlaces.length} found</span>}
          </div>
          <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="image-upload-input" />
        </label>

        <div className="flex-1">
          <button type="button" onClick={() => setInputMode(inputMode === "url" ? null : "url")}
            aria-expanded={inputMode === "url"} aria-controls="wishlist-link"
            className="p-4 w-full text-left transition-all flex items-center gap-3 cursor-pointer hover:shadow-sm"
            style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4 }}>
            <LinkIcon className="w-5 h-5" style={{ color: "var(--zen-accent)" }} />
            <div>
              <p className="font-medium text-sm" style={{ color: "var(--zen-text)" }}>Add a link</p>
              <p className="text-xs" style={{ color: "var(--zen-sub)" }}>Travel blog, article</p>
            </div>
          </button>
        </div>
      </div>

      {inputMode === "url" && (
        <input id="wishlist-link" aria-label="Link to your travel ideas" type="url" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="Paste your link here..."
          className="zen-search" style={{ paddingLeft: 14 }}
          data-testid="url-input" autoFocus />
      )}

      {sourceError && (
        <p role="alert" className="text-sm" style={{ color: "var(--zen-accent)" }} data-testid="source-error">{sourceError}</p>
      )}

      {/* A wishlist is optional; an empty field still allows continuing. */}
      <button onClick={saveAndContinue} disabled={isReadingLink || isUploading}
        className="zen-btn zen-btn-primary w-full py-4"
        data-testid="input-continue-btn">
        {isReadingLink ? "Reading your link…" : <>Continue <ChevronRight className="w-5 h-5" /></>}
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1" style={{ background: "var(--zen-border)" }} />
        <span className="font-mono-label text-[10px]" style={{ color: "var(--zen-dim)" }}>or</span>
        <div className="h-px flex-1" style={{ background: "var(--zen-border)" }} />
      </div>

      {/* Let Japan unfold */}
      <button onClick={handleUnfold}
        className="w-full p-5 text-left transition-all hover:shadow-md group"
        style={{ background: "var(--zen-s1)", border: "1.5px solid var(--zen-accent)", borderRadius: 4 }}
        data-testid="unfold-btn">
        <div className="flex items-center gap-3">
          <Compass className="w-6 h-6 shrink-0" style={{ color: "var(--zen-accent)" }} />
          <div>
            <p className="font-semibold text-base" style={{ color: "var(--zen-text)" }}>Let Japan unfold for you</p>
            <p className="text-xs mt-0.5" style={{ color: "var(--zen-sub)" }}>
              No list yet? Start with our suggestions and make them yours.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 shrink-0 ml-auto opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: "var(--zen-accent)" }} />
        </div>
      </button>

      {/* Skip to menu */}
      <button onClick={skipInput}
        className="w-full text-center py-3 text-xs transition-opacity hover:opacity-70"
        style={{ color: "var(--zen-dim)" }}
        data-testid="skip-input-btn">
        Browse places instead
      </button>
    </FormShell>
  );
};

export default InputPage;
